import { useState } from 'react';
import { ArrowRight, RotateCcw, Share2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';

export const EstimationGame = ({ 
  items, 
  title,
  introText,
  unit = '€',
  relatedLinks = []
}) => {
  const [currentItem, setCurrentItem] = useState(0);
  const [userEstimate, setUserEstimate] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [itemResult, setItemResult] = useState(null);
  const [totalScore, setTotalScore] = useState(0);
  const [allResults, setAllResults] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameFinished, setGameFinished] = useState(false);

  const handleStart = () => {
    setGameStarted(true);
    setUserEstimate(items[0].minValue.toString());
  };

  const calculateScore = (estimate, actual) => {
    const difference = Math.abs(estimate - actual);
    const percentDiff = (difference / actual) * 100;
    
    if (percentDiff <= 5) return 100;
    if (percentDiff <= 10) return 80;
    if (percentDiff <= 20) return 60;
    if (percentDiff <= 30) return 40;
    if (percentDiff <= 50) return 20;
    return 0;
  };

  const handleSubmit = () => {
    const estimate = parseInt(userEstimate.replace(/\D/g, ''));
    const actual = items[currentItem].actualValue;
    const score = calculateScore(estimate, actual);
    
    const result = {
      item: items[currentItem],
      estimate,
      actual,
      score,
      difference: estimate - actual,
      percentDiff: ((estimate - actual) / actual) * 100
    };
    
    setItemResult(result);
    setShowResult(true);
    setTotalScore(prev => prev + score);
    setAllResults(prev => [...prev, result]);
  };

  const handleNext = () => {
    if (currentItem < items.length - 1) {
      setCurrentItem(prev => prev + 1);
      setUserEstimate(items[currentItem + 1].minValue.toString());
      setShowResult(false);
      setItemResult(null);
    } else {
      setGameFinished(true);
    }
  };

  const handleRestart = () => {
    setCurrentItem(0);
    setUserEstimate('');
    setShowResult(false);
    setItemResult(null);
    setTotalScore(0);
    setAllResults([]);
    setGameStarted(false);
    setGameFinished(false);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('de-DE').format(num);
  };

  const item = items[currentItem];
  const progress = ((currentItem + 1) / items.length) * 100;
  const maxPossibleScore = items.length * 100;
  const finalPercentage = Math.round((totalScore / maxPossibleScore) * 100);

  // Intro screen
  if (!gameStarted) {
    return (
      <div className="quiz-container" data-testid="estimation-intro">
        <div className="text-center py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6 font-[var(--font-heading)]">
            {title}
          </h1>
          {introText && (
            <p className="text-lg text-[#475569] mb-8 max-w-xl mx-auto">
              {introText}
            </p>
          )}
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 mb-8 max-w-md mx-auto">
            <div className="text-5xl font-bold text-[#F97316] mb-2 font-[var(--font-heading)]">
              {items.length}
            </div>
            <div className="text-[#475569]">Objekte zum Schätzen</div>
          </div>
          <button
            onClick={handleStart}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#F97316] text-white font-semibold rounded-lg hover:bg-[#EA580C] transition-colors duration-200"
            data-testid="estimation-start-button"
          >
            Schätzspiel starten
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    );
  }

  // Final result screen
  if (gameFinished) {
    return (
      <div className="quiz-container" data-testid="estimation-final-result">
        <div className="result-container animate-scaleIn">
          <div className="score-reveal">
            <div className="result-score" style={{ color: '#F97316' }}>
              {finalPercentage}%
            </div>
            <div className="result-label">Treffsicherheit</div>
          </div>
          
          <div className="text-left bg-[#F8FAFC] rounded-lg p-4 mb-6 max-w-lg mx-auto">
            <h3 className="font-semibold mb-3">Deine Schätzungen:</h3>
            {allResults.map((result, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-[#E2E8F0] last:border-0">
                <span className="text-sm text-[#475569] truncate mr-2">{result.item.title}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-sm font-medium ${result.score >= 60 ? 'text-green-600' : result.score >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {result.score} Pkt.
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleRestart}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#F97316] text-[#F97316] font-semibold rounded-lg hover:bg-[#F97316]/5 transition-colors duration-200"
              data-testid="estimation-restart-button"
            >
              <RotateCcw size={18} />
              Nochmal spielen
            </button>
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: title,
                    text: `Ich habe ${finalPercentage}% Treffsicherheit beim "${title}" Schätzspiel auf tippspiel.it erreicht!`,
                    url: window.location.href,
                  });
                }
              }}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#F97316] text-white font-semibold rounded-lg hover:bg-[#EA580C] transition-colors duration-200"
              data-testid="estimation-share-button"
            >
              <Share2 size={18} />
              Ergebnis teilen
            </button>
          </div>

          {relatedLinks.length > 0 && (
            <div className="internal-links mt-8">
              <div className="internal-links-title">Das könnte dich auch interessieren:</div>
              <div className="internal-links-list">
                {relatedLinks.map((link, index) => (
                  <Link 
                    key={index} 
                    to={link.path} 
                    className="internal-link"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-container" data-testid="estimation-game">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-[#475569] mb-2">
          <span>Objekt {currentItem + 1} von {items.length}</span>
          <span>Punkte: {totalScore}</span>
        </div>
        <div className="quiz-progress">
          <div 
            className="quiz-progress-bar progress-bar" 
            style={{ width: `${progress}%`, backgroundColor: '#F97316' }}
          />
        </div>
      </div>

      {/* Item Card */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
        <div className="relative">
          <img 
            src={item.image} 
            alt={item.title}
            className="w-full h-64 md:h-80 object-cover"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
            <h2 className="text-xl md:text-2xl font-bold text-white font-[var(--font-heading)]">
              {item.title}
            </h2>
          </div>
        </div>

        <div className="p-6 md:p-8">
          {item.description && (
            <p className="text-[#475569] mb-6">{item.description}</p>
          )}

          {item.details && (
            <div className="flex flex-wrap gap-3 mb-6">
              {item.details.map((detail, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-[#F1F5F9] rounded-full text-sm text-[#475569]"
                >
                  {detail}
                </span>
              ))}
            </div>
          )}

          {!showResult ? (
            <>
              <label className="block text-sm font-medium text-[#0F172A] mb-3">
                {item.question || `Was schätzt du? (in ${unit})`}
              </label>
              
              <div className="mb-4">
                <input
                  type="range"
                  min={item.minValue}
                  max={item.maxValue}
                  step={item.step || 10000}
                  value={userEstimate || item.minValue}
                  onChange={(e) => setUserEstimate(e.target.value)}
                  className="estimation-slider w-full"
                  data-testid="estimation-slider"
                />
                <div className="flex justify-between text-xs text-[#94A3B8] mt-1">
                  <span>{formatNumber(item.minValue)} {unit}</span>
                  <span>{formatNumber(item.maxValue)} {unit}</span>
                </div>
              </div>

              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-[#F97316] font-[var(--font-heading)]">
                  {formatNumber(parseInt(userEstimate || item.minValue))} {unit}
                </div>
                <div className="text-sm text-[#94A3B8]">Deine Schätzung</div>
              </div>

              <button
                onClick={handleSubmit}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#F97316] text-white font-semibold rounded-lg hover:bg-[#EA580C] transition-colors duration-200"
                data-testid="estimation-submit-button"
              >
                Schätzung abgeben
                <ArrowRight size={18} />
              </button>
            </>
          ) : (
            <div className="animate-fadeInUp">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-[#F1F5F9] rounded-lg p-4 text-center">
                  <div className="text-sm text-[#64748B] mb-1">Deine Schätzung</div>
                  <div className="text-xl font-bold text-[#0F172A]">
                    {formatNumber(itemResult.estimate)} {unit}
                  </div>
                </div>
                <div className="bg-[#0284C7]/10 rounded-lg p-4 text-center">
                  <div className="text-sm text-[#0284C7] mb-1">Tatsächlicher Wert</div>
                  <div className="text-xl font-bold text-[#0284C7]">
                    {formatNumber(itemResult.actual)} {unit}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-3 mb-6 p-4 bg-[#F8FAFC] rounded-lg">
                {itemResult.difference > 0 ? (
                  <TrendingUp className="text-red-500" size={24} />
                ) : itemResult.difference < 0 ? (
                  <TrendingDown className="text-green-500" size={24} />
                ) : (
                  <Minus className="text-[#0284C7]" size={24} />
                )}
                <span className="text-lg">
                  {itemResult.difference === 0 ? (
                    <span className="text-[#0284C7] font-semibold">Perfekt getroffen!</span>
                  ) : (
                    <>
                      <span className={itemResult.difference > 0 ? 'text-red-600' : 'text-green-600'}>
                        {formatNumber(Math.abs(itemResult.difference))} {unit}
                      </span>
                      <span className="text-[#64748B]">
                        {' '}({itemResult.difference > 0 ? 'zu hoch' : 'zu niedrig'})
                      </span>
                    </>
                  )}
                </span>
              </div>

              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-[#F97316] font-[var(--font-heading)] score-reveal">
                  +{itemResult.score}
                </div>
                <div className="text-sm text-[#64748B]">Punkte für diese Runde</div>
              </div>

              {item.explanation && (
                <div className="bg-[#F8FAFC] rounded-lg p-4 mb-6">
                  <p className="text-sm text-[#475569]">{item.explanation}</p>
                </div>
              )}

              <button
                onClick={handleNext}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0284C7] text-white font-semibold rounded-lg hover:bg-[#0369A1] transition-colors duration-200"
                data-testid="estimation-next-button"
              >
                {currentItem < items.length - 1 ? 'Nächstes Objekt' : 'Ergebnis anzeigen'}
                <ArrowRight size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EstimationGame;
